<template>
  <div class="coal-stock-3d">
    <p class="cs3-hint">
      {{ statusText }}
    </p>
    <div v-if="emptyHint" class="cs3-empty-banner">
      <p>{{ emptyHint }}</p>
    </div>
    <div v-if="sceneError" class="cs3-empty-banner cs3-error">
      <p>{{ sceneError }}</p>
    </div>
    <div
      id="coal-stock-3d-host"
      ref="threeHost"
      class="cs3-three-wrap"
      :class="{ 'is-dimmed': emptyHint || sceneError }"
    >
      <div class="cs3-zoom-bar" aria-label="视角缩放">
        <button type="button" class="cs3-zoom-btn" title="放大" @click="onZoomIn">+</button>
        <button type="button" class="cs3-zoom-btn" title="缩小" @click="onZoomOut">−</button>
      </div>
    </div>

    <el-dialog
      :visible.sync="detailVisible"
      :title="detailTitle"
      width="440px"
      append-to-body
      @close="onDetailClose"
    >
      <dl class="cs3-dl">
        <template v-for="(row, idx) in detailRows">
          <div :key="idx" class="cs3-dl-pair">
            <dt>{{ row.label }}</dt>
            <dd>{{ row.value }}</dd>
          </div>
        </template>
      </dl>
    </el-dialog>
  </div>
</template>

<script>
import { storePage, storeAreaListByMainId, storeSubPage } from "./api";

function formatTons(n) {
  const x = Math.round(n * 100) / 100;
  return x >= 10000 ? x.toLocaleString("zh-CN", { maximumFractionDigits: 0 }) : String(x);
}

export default {
  name: "CoalStock3DView",
  data() {
    return {
      loading: false,
      statusText: "",
      emptyHint: "",
      sceneError: "",
      sceneApi: null,
      dim: { ew: 80, ns: 360, clearHeight: 30 },
      detailVisible: false,
      detailTitle: "堆位详情",
      detailRows: [],
    };
  },
  mounted() {
    this.loadData();
  },
  activated() {
    this.$nextTick(() => {
      if (this.sceneApi && typeof this.sceneApi.resize === "function") {
        this.sceneApi.resize();
        requestAnimationFrame(() => this.sceneApi.resize());
      }
    });
  },
  beforeDestroy() {
    if (this.sceneApi) {
      this.sceneApi.dispose();
      this.sceneApi = null;
    }
  },
  methods: {
    scheduleSceneResize() {
      const run = () => {
        if (this.sceneApi && typeof this.sceneApi.resize === "function") {
          this.sceneApi.resize();
        }
      };
      run();
      this.$nextTick(run);
      requestAnimationFrame(run);
      requestAnimationFrame(() => requestAnimationFrame(run));
      setTimeout(run, 50);
      setTimeout(run, 200);
    },
    formatTons,
    onZoomIn() {
      if (this.sceneApi) this.sceneApi.zoomIn();
    },
    onZoomOut() {
      if (this.sceneApi) this.sceneApi.zoomOut();
    },
    mapSubRow(row) {
      const disabled = row.status === 0 || row.status === "0";
      const w = Math.max(Number(row.layoutW) || 1, 0.1);
      const h = Math.max(Number(row.layoutH) || 1, 0.1);
      const capDb = row.stockCapTons != null && row.stockCapTons !== "" ? Number(row.stockCapTons) : NaN;
      const capacityTons = Number.isFinite(capDb) && capDb > 0 ? capDb : undefined;
      return {
        id: "s_" + row.id,
        dbId: row.id,
        name: row.locationName || row.locationCode || "堆位",
        x: Number(row.layoutX) || 0,
        y: Number(row.layoutY) || 0,
        w,
        h,
        capacityTons,
        inventory: disabled ? 0 : Number(row.stockAmount) || 0,
        customer: row.custName || "—",
        coalType: row.varNo || "—",
        contractNo:
          row.contractExtId != null && String(row.contractExtId).trim() !== ""
            ? String(row.contractExtId)
            : "—",
        contractName: "—",
        locationCode: row.locationCode || "",
        _disabled: disabled,
      };
    },
    async fetchShedZones(store) {
      const mainId = String(store.id);
      const areasRes = await storeAreaListByMainId(mainId);
      const ar = areasRes && areasRes.data ? areasRes.data : {};
      let areas = ar.data != null ? ar.data : ar.records || [];
      if (!Array.isArray(areas)) areas = [];
      const zones = [];
      for (const a of areas) {
        const subRes = await storeSubPage({
          page: 1,
          size: 2000,
          mainId,
          areaId: a.id,
        });
        const sr = subRes && subRes.data ? subRes.data : {};
        const subPage = sr.data != null ? sr.data : sr;
        let subs = subPage.records || subPage.list || [];
        if (!Array.isArray(subs)) subs = [];
        zones.push({
          id: "z_" + a.id,
          name: a.areaName || "库区",
          x: Number(a.posX) || 0,
          y: Number(a.posY) || 0,
          w: Number(a.posW) || 0,
          h: Number(a.posH) || 0,
          stacks: subs.map((row) => this.mapSubRow(row)),
        });
      }
      return zones;
    },
    async buildLayoutModel(westStore, eastStore) {
      const westTitle = westStore ? westStore.storeName || westStore.storeNo || "西煤棚" : "西煤棚";
      const eastTitle = eastStore ? eastStore.storeName || eastStore.storeNo || "东煤棚" : "东煤棚";
      const westZones = westStore ? await this.fetchShedZones(westStore) : [];
      const eastZones = eastStore ? await this.fetchShedZones(eastStore) : [];
      return {
        west: { title: westTitle, zones: westZones },
        east: { title: eastTitle, zones: eastZones },
      };
    },
    onStackPick(detail) {
      const s = detail.stack;
      const inv = s._disabled ? 0 : Number(s.inventory) || 0;
      const capDb = Number(s.capacityTons);
      const cap =
        Number.isFinite(capDb) && capDb > 0 ? capDb : s.w * s.h * 30 * 1;
      const fill = cap > 0 ? Math.min(100, Math.round((inv / cap) * 1000) / 10) : 0;
      const capSrc =
        Number.isFinite(capDb) && capDb > 0 ? "堆位维护的上限" : "占地×30m×1t/m³";
      this.detailTitle = (s.name || "堆位") + " · 详情";
      this.detailRows = [
        { label: "所属煤棚", value: detail.shedName || "—" },
        { label: "所属库区", value: detail.zoneName || "—" },
        { label: "堆位名称", value: s.name || s.id || "—" },
        { label: "库位编码", value: s.locationCode || "—" },
        { label: "状态", value: s._disabled ? "已禁用" : "有效" },
        { label: "客户", value: s.customer || "—" },
        { label: "物资/煤种", value: s.coalType || "—" },
        { label: "合同扩展ID", value: s.contractNo || "—" },
        { label: "合同名称", value: s.contractName || "—" },
        { label: "库存量", value: formatTons(inv) + " 吨" },
        { label: "库存容量上限", value: formatTons(cap) + " 吨（" + capSrc + "）" },
        { label: "库容占用", value: fill + "%" },
      ];
      this.detailVisible = true;
    },
    onDetailClose() {
      this.detailRows = [];
    },
    async loadData() {
      this.loading = true;
      this.emptyHint = "";
      this.sceneError = "";
      try {
        const res = await storePage({ page: 1, size: 500, storeType: 1, status: 1 });
        const body = res && res.data ? res.data : {};
        const page = body.data != null ? body.data : body;
        let list = page.records || page.list || [];
        if (!Array.isArray(list)) list = [];
        list = list.filter((s) => s && (s.status === 1 || s.status === "1"));
        list.sort((a, b) => String(a.storeNo || "").localeCompare(String(b.storeNo || ""), "zh-Hans-CN"));
        const westStore = list[0] || null;
        const eastStore = list.length >= 2 ? list[1] : null;
        if (!westStore && !eastStore) {
          this.emptyHint = "未获取到料场煤棚（base_store 中 store_type=1 且有效）。请维护数据后刷新。";
          if (this.sceneApi) this.sceneApi.dispose();
          this.sceneApi = null;
          this.statusText = "";
          return;
        }
        await this.$nextTick();
        await this.$nextTick();
        let host = this.$refs.threeHost;
        if (!host) {
          await new Promise((resolve) => requestAnimationFrame(resolve));
          host = this.$refs.threeHost;
        }
        if (!host && typeof document !== "undefined") {
          host = document.getElementById("coal-stock-3d-host");
        }
        if (!host) {
          this.sceneError = "画布容器未挂载，请刷新页面重试。";
          return;
        }
        let createCoalStock3dScene;
        try {
          const mod = await import(/* webpackChunkName: "stock-3d-scene" */ "./coalStock3dScene.js");
          createCoalStock3dScene = mod.createCoalStock3dScene;
        } catch (err) {
          console.error(err);
          this.sceneError =
            "3D 模块加载失败（请确认已执行 npm install 且 three 依赖完整）。详情见控制台。";
          return;
        }
        if (!this.sceneApi) {
          this.sceneApi = createCoalStock3dScene(host, {
            onPick: this.onStackPick,
          });
        }
        const model = await this.buildLayoutModel(westStore, eastStore);
        try {
          this.sceneApi.init();
          this.sceneApi.setLayoutModel(model);
        } catch (err) {
          console.error(err);
          this.sceneError = "3D 场景初始化失败（可能为 WebGL 不支持）。详情见控制台。";
          if (this.sceneApi) {
            try {
              this.sceneApi.dispose();
            } catch (e2) {
              /* ignore */
            }
            this.sceneApi = null;
          }
          return;
        }
        const cnt = this.sceneApi.collectStackCount();
        const c = this.sceneApi.getConstants();
        this.dim = { ew: c.ew, ns: c.ns, clearHeight: c.clearHeight };
        await this.$nextTick();
        this.scheduleSceneResize();
        this.statusText =
          (westStore ? "左棚 " + (westStore.storeName || westStore.storeNo) : "") +
          (eastStore ? " · 右棚 " + (eastStore.storeName || eastStore.storeNo) : "") +
          " · 共 " +
          cnt +
          " 个堆位";
      } catch (e) {
        console.error(e);
        this.emptyHint = "加载失败，请检查接口或网络。";
        this.statusText = "";
        if (this.sceneApi) {
          try {
            this.sceneApi.dispose();
          } catch (e2) {
            /* ignore */
          }
          this.sceneApi = null;
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.coal-stock-3d {
  display: flex;
  flex-direction: column;
  min-height: #{"min(560px, calc(100vh - 120px))"};
  background: #eef2f7;
  padding: 0 0 16px;
  box-sizing: border-box;
}

.cs3-header {
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  padding: 14px 20px 16px;
}

.cs3-crumb {
  font-size: 12px;
  color: #94a3b8;
  margin-bottom: 6px;
}

.cs3-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #1e293b;
}

.cs3-sub {
  font-size: 13px;
  color: #64748b;
  margin: 8px 0 0;
  max-width: 960px;
  line-height: 1.55;
}

.cs3-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.cs3-link {
  color: #3b5bdb;
  text-decoration: none;
  font-size: 13px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
}

.cs3-link:hover {
  background: #eef2ff;
  border-color: #3b5bdb;
}

.cs3-status {
  font-size: 12px;
  color: #94a3b8;
  margin-left: auto;
}

.cs3-hint {
  margin: 12px 20px 0;
  padding: 10px 12px;
  font-size: 12px;
  color: #64748b;
  background: #f9fafb;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  line-height: 1.5;
}

.cs3-empty-banner {
  margin: 12px 20px 0;
  padding: 12px 16px;
  text-align: center;
  color: #606266;
  background: #fdf6ec;
  border-radius: 8px;
  border: 1px solid #faecd8;
  font-size: 13px;
}

.cs3-empty-banner.cs3-error {
  background: #fef0f0;
  border-color: #fde2e2;
  color: #f56c6c;
}

.cs3-three-wrap {
  flex: 1 1 auto;
  flex-shrink: 0;
  min-height: #{"min(420px, 52vh)"};
  height: #{"min(720px, max(360px, 52vh))"};
  max-height: #{"min(800px, 70vh)"};
  margin: 16px 20px 0;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: linear-gradient(165deg, #dbe4f0 0%, #e8eef6 45%, #dce5f0 100%);
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.07);
  position: relative;
  overflow: hidden;
}

.cs3-three-wrap.is-dimmed {
  opacity: 0.35;
  pointer-events: none;
}

.cs3-zoom-bar {
  position: absolute;
  right: 14px;
  bottom: 14px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cs3-zoom-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.55);
  background: rgba(255, 255, 255, 0.92);
  color: #334155;
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
}

.cs3-zoom-btn:hover {
  background: #eef2ff;
  border-color: rgba(59, 91, 219, 0.45);
  color: #3b5bdb;
}

.cs3-dl {
  margin: 0;
}

.cs3-dl-pair {
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 10px 14px;
  font-size: 13px;
  line-height: 1.5;
  align-items: start;
}

.cs3-dl-pair + .cs3-dl-pair {
  margin-top: 10px;
}

.cs3-dl dt {
  color: #94a3b8;
  font-weight: 500;
}

.cs3-dl dd {
  margin: 0;
  color: #1e293b;
  word-break: break-word;
}
</style>
